import { useId } from "react";
import { v4 } from "uuid";

import ExportablesTemplateHeaderSection from "@/components/AIComponents/ExportablesModal/templates/components/ExportablesTemplateHeaderSection";
import OrganizerDirections from "@/components/AIComponents/ExportablesModal/templates/components/OrganizerDirections/OrganizerDirections";
import * as styles from "./MainIdeaOrganizer.styles";

import type { TemplateProps } from "@/components/AIComponents/ExportablesModal/templates/registry";

export interface MainIdeaFormData {
  article_title: string;
  main_idea: string;
  directions: string;
  detail1: string;
  detail2: string;
  detail3: string;
}

const MainIdeaOrganizer = ({
  formData,
  onChange,
  title = "Main idea and details",
  isStudent = false,
}: TemplateProps<MainIdeaFormData>) => {
  const id = useId();
  const MAIN_IDEA_MAX_LENGTH = 125; // Updated per Jon's specification
  const MAIN_IDEA_DETAIL_MAX_LENGTH = 250; // New constant for detail max length
  const getDefaultDirections = (articleTitle: string) =>
    `Read the article "${articleTitle}" and identify three key details that support the main idea noted below. Explain how each detail you selected supports the main idea.`;
  const {
    article_title: articleTitle = "",
    main_idea: mainIdea = "",
    directions = getDefaultDirections(articleTitle),
    detail1 = "",
    detail2 = "",
    detail3 = "",
  } = formData || {};

  const detailKeys = [
    { key: "detail1", value: detail1 },
    { key: "detail2", value: detail2 },
    { key: "detail3", value: detail3 },
  ] as const;

  const handleChange = (field: keyof MainIdeaFormData, value: string) => {
    if (onChange) {
      const currentDirections =
        formData?.directions ||
        getDefaultDirections(formData?.article_title ?? "");
      onChange({
        article_title: formData?.article_title ?? "",
        main_idea: formData?.main_idea ?? "",
        directions: field === "directions" ? value : currentDirections,
        detail1: formData?.detail1 ?? "",
        detail2: formData?.detail2 ?? "",
        detail3: formData?.detail3 ?? "",
        [field]: value,
      });
    }
  };

  // --- Render helpers for each section ---
  const renderTitleSection = () => (
    <ExportablesTemplateHeaderSection
      templateTitle={title}
      articleTitle={articleTitle}
      directions={
        <OrganizerDirections
          name="Main Idea"
          value={directions}
          handleChange={(e) => handleChange("directions", e.target.value)}
          isStudent={isStudent}
        />
      }
    />
  );

  const renderMainIdeaSection = () => (
    <div css={styles.mainIdeaBox}>
      <label htmlFor="main-idea">Main Idea</label>
      {isStudent ? (
        <p css={styles.mainIdeaParagraph}>{mainIdea}</p>
      ) : (
        <textarea
          data-qa-selector="main-idea"
          id={id}
          value={mainIdea}
          onChange={(e) => handleChange("main_idea", e.target.value)}
          css={styles.mainIdeaTextarea}
          aria-label="Main Idea"
          maxLength={MAIN_IDEA_MAX_LENGTH}
          placeholder="Write a clear, concise main idea"
        />
      )}
      <div css={styles.characterCount}>
        {mainIdea.length}/{MAIN_IDEA_MAX_LENGTH} characters
      </div>
    </div>
  );

  const renderDetailsSection = () => (
    <div css={styles.detailsRow}>
      {detailKeys.map(({ key, value }, idx) => (
        <div css={styles.detailBox} key={key}>
          <label htmlFor={`detail-${idx + 1}`}>{`Detail #${idx + 1}`}</label>
          <textarea
            data-qa-selector={`detail-${idx + 1}`}
            id={`detail-${idx + 1}-${v4()}`}
            css={styles.mainIdeaTextarea}
            value={value}
            onChange={(e) => handleChange(key, e.target.value)}
            aria-label={`Detail #${idx + 1}`}
            maxLength={MAIN_IDEA_DETAIL_MAX_LENGTH}
          />
        </div>
      ))}
    </div>
  );

  return (
    <form css={styles.mainIdeaGrid} aria-label="Main Idea Organizer">
      {renderTitleSection()}
      {renderMainIdeaSection()}
      {renderDetailsSection()}
    </form>
  );
};

export default MainIdeaOrganizer;
